import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import {
  Drawer,
  List,
  ListItem,
  ListItemText,
  Divider,
  useTheme,
  useMediaQuery,
  Collapse,
  CircularProgress,
  Link,
  Box,
} from '@mui/material';
import {
  ExpandMore,
  ExpandLess,
} from '@mui/icons-material';
import SettingsContext from '../../contexts/SettingsContext';
import { useWriters, usePublications, useCategories } from '../../hooks/useProducts';

const useStyles = makeStyles((theme) => ({
  root: {
    width: '100%',
    maxWidth: theme.mixins.drawer.minWidth,
  },
  appBar: {
    width: `calc(100% - ${theme.mixins.drawer.minWidth}px)`,
    marginLeft: theme.mixins.drawer.minWidth,
  },
  drawer: {
    width: theme.mixins.drawer.minWidth,
    maxWidth: theme.mixins.drawer.minWidth,
    flexShrink: 0,
  },
  drawerPaper: {
    background: theme.palette.background.default,
    width: theme.mixins.drawer.minWidth,
    border: 0,
  },
  toolbar: {
    ...theme.mixins.toolbar,
    minHeight: '100px !important',
  },
  parentList: {
    paddingTop: '20px !important',
  },
  nested: {
    width: '80%',
    margin: '5px auto',
    paddingLeft: '40px !important',
    '&:hover': {
      background: theme.palette.secondary.light,
      borderRadius: 58.5,
    },
  },
  activeListItem: {
    width: '90%',
    margin: '0 auto',
    background: theme.custom.palette.navDrawer?.activeItem || 
      (theme.palette.mode === 'dark' ? '#FFEB3B' : '#FFF176'),
    color: theme.palette.mode === 'dark' ? '#000000' : 'inherit',
    borderRadius: 58.5,
    paddingLeft: '40px !important',
    '&:hover': {
      background: theme.custom.palette.navDrawer?.activeItem || 
        (theme.palette.mode === 'dark' ? '#FFEB3B' : '#FFF176'),
      borderRadius: 58.5,
    },
    '& .MuiListItemText-primary': {
      fontWeight: 600,
    },
  },
  listItems: {
    flexDirection: 'column',
    paddingLeft: 35,
    paddingRight: 30,
  },
  sectionHeader: {
    paddingLeft: '30px !important',
    '& .MuiListItemText-primary': {
      fontSize: '1rem !important',
      fontFamily: theme.custom?.fontFamily?.metropolis?.fontFamily || '"Metropolis", "Arial", sans-serif',
      letterSpacing: '0.015rem',
      fontWeight: 600,
    },
  },
  sectionItem: {
    '& .MuiListItemText-primary': {
      fontSize: '0.8rem !important',
      fontFamily: theme.custom?.fontFamily?.metropolis?.fontFamily || '"Metropolis", "Arial", sans-serif',
      letterSpacing: '0.015rem',
      fontWeight: 400,
    },
  },
  moreLink: {
    margin: '10px 35px 15px 40px !important',
    padding: '0 !important',
    fontSize: '0.85rem !important',
    textTransform: 'none !important',
    color: `${theme.palette.text.link || theme.palette.primary.main} !important`,
    cursor: 'pointer !important',
    textDecoration: 'none !important',
    display: 'inline-block !important',
    fontWeight: 500,
    fontFamily: theme.custom?.fontFamily?.metropolis?.fontFamily || '"Metropolis", "Arial", sans-serif',
    '&:hover': {
      color: `${theme.custom?.palette?.navDrawer?.linkHover || theme.palette.secondary.main} !important`,
      textDecoration: 'underline !important',
      backgroundColor: 'transparent !important',
    },
  },
  backdrop: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    zIndex: theme.zIndex.drawer - 1,
    display: 'none',
    '&.visible': {
      display: 'block',
    },
  },
}));

const ITEMS_PER_SECTION = 5;

export default function NavDrawer() {
  const classes = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('xs'));
  const settings = useContext(SettingsContext);
  
  const drawerRef = useRef(null);
  const backdropRef = useRef(null);

  const [openSection, setOpenSection] = useState('writers');

  const { writers, loading: writersLoading, error: writersError } = useWriters();
  const { publications, loading: publicationsLoading, error: publicationsError } = usePublications();
  const { categories, loading: categoriesLoading, error: categoriesError } = useCategories();

  // Close drawer when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Don't close if drawer is not open
      if (!settings.navDrawerOpen) return;
      
      // Don't close if click is on the drawer itself
      if (drawerRef.current && drawerRef.current.contains(event.target)) {
        return;
      }
      
      // Don't close if click is on the backdrop
      if (backdropRef.current && backdropRef.current.contains(event.target)) {
        return;
      }
      
      // Don't close if click is on the hamburger menu button
      const menuButton = document.querySelector('[aria-label="open drawer"]');
      if (menuButton && menuButton.contains(event.target)) {
        return;
      }
      
      // Close the drawer
      settings.setNavDrawer(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [settings, settings.navDrawerOpen]);

  // Close drawer on route change
  useEffect(() => {
    if (settings.navDrawerOpen && isMobile) {
      settings.setNavDrawer(false);
    }
  }, [location.pathname, isMobile, settings]);

  const handleToggle = (section) => {
    setOpenSection(openSection === section ? null : section);
  };

  const handleLinkClick = (url) => {
    navigate(url);
    // Close drawer on mobile after clicking a link
    if (isMobile && settings.navDrawerOpen) {
      settings.setNavDrawer(false);
    }
  };

  const handleMoreButtonClick = (pathPrefix) => {
    navigate(`/${pathPrefix}`);
    // Close drawer on mobile after clicking a link
    if (isMobile && settings.navDrawerOpen) {
      settings.setNavDrawer(false);
    }
  };

  const renderNavList = (title, sectionKey, items, loading, error, pathPrefix) => {
    const limitedItems = items?.slice(0, ITEMS_PER_SECTION) || [];
    const isOpen = openSection === sectionKey;

    return (
      <>
        <ListItem 
          button 
          onClick={() => handleToggle(sectionKey)} 
          className={classes.sectionHeader}
        >
          <ListItemText primary={title} />
          {isOpen ? <ExpandLess /> : <ExpandMore />}
        </ListItem>
        <Collapse in={isOpen} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {loading ? (
              <CircularProgress size={20} style={{ margin: '10px 30px' }} />
            ) : error ? (
              <ListItemText 
                primary="লোড করতে ব্যর্থ হয়েছে" 
                style={{ paddingLeft: '32px' }} 
              />
            ) : (
              <>
                {limitedItems.map((item) => {
                  const url = `/${pathPrefix}/${item.id}`;
                  const isActive = location.pathname === url;
                  return (
                    <ListItem
                      button
                      key={item.id}
                      className={isActive ? classes.activeListItem : `${classes.nested} ${classes.sectionItem}`}
                      onClick={() => handleLinkClick(url)}
                    >
                      <ListItemText 
                        primary={item.name} 
                      />
                    </ListItem>
                  );
                })}
                {!loading && !error && (
                  <Link 
                    component="button"
                    className={classes.moreLink}
                    onClick={() => handleMoreButtonClick(pathPrefix)}
                    variant="body2"
                  >
                    আরও দেখুন...
                  </Link>
                )}
              </>
            )}
          </List>
        </Collapse>
        <Divider />
      </>
    );
  };

  return (
    <>
      {/* Backdrop for mobile */}
      {isMobile && settings.navDrawerOpen && (
        <Box 
          ref={backdropRef}
          className={`${classes.backdrop} visible`}
          onClick={() => settings.setNavDrawer(false)}
        />
      )}
      
      <div className={classes.root} ref={drawerRef}>
        <Drawer
          variant={isMobile ? 'temporary' : 'persistent'}
          anchor="left"
          open={settings.navDrawerOpen}
          onClose={() => settings.setNavDrawer(false)}
          classes={{ paper: classes.drawerPaper }}
          ModalProps={{
            BackdropProps: {
              invisible: false,
            },
          }}
        >
          <div className={classes.toolbar} />
          <List component="nav" className={classes.parentList}>
            {renderNavList('লেখক', 'writers', writers, writersLoading, writersError, 'author')}
            {renderNavList('প্রকাশনী', 'publications', publications, publicationsLoading, publicationsError, 'publication')}
            {renderNavList('বিষয়', 'categories', categories, categoriesLoading, categoriesError, 'category')}
          </List>
        </Drawer>
      </div>
    </>
  );
}
